import { selectRosters } from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { IExtendedRoster } from '@src/features/shift-post-v2/state/shift-post-v2.types'
import { IAcceptanceCriteriaFormValues } from '@src/features/shift-post-v2/steps/acceptance-criteria-step/acceptance-criteria-step.types'
import { IDropdownData } from '@src/models/Common'
import { FormikProps } from 'formik'
import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

// Define the dropdown option type
export interface DropdownOption {
  label: string
  value: string
}

export const useRosterSelection = (formik: FormikProps<IAcceptanceCriteriaFormValues>) => {
  const { setFieldValue, values } = formik
  const autoAcceptRosters = values.auto_accept_rosters

  const [selectedRosters, setSelectedRosters] = useState<IDropdownData[]>([]) // Local state - handles dropdown values
  const rosters = useSelector(selectRosters)

  useEffect(() => {
    setSelectedRosters(
      autoAcceptRosters.map((roster) => ({
        value: roster.id,
        label: roster.name,
      }))
    )
  }, [autoAcceptRosters])

  const setRosterFields = (rosters: IExtendedRoster[]) => {
    setFieldValue('auto_accept_rosters', rosters)
    setFieldValue(
      'roster_ids',
      rosters.map((roster) => roster.id)
    )
    setFieldValue(
      'auto_accept_roster_ids',
      rosters.map((roster) => roster.id)
    )
  }

  // Remove tag
  const handleRemoveTag = (value: number) => {
    const updatedRosters = values.auto_accept_rosters.filter((roster) => roster.id !== value)
    setRosterFields(updatedRosters)
  }

  // Add dropdown option
  const handleAddDropdownOption = (option: IDropdownData) => {
    const rosterToAdd = rosters.find((roster) => roster.id === option.value)
    if (rosterToAdd && !autoAcceptRosters.some((roster) => roster.id === option.value)) {
      const updatedRosters = [...autoAcceptRosters, rosterToAdd]
      setRosterFields(updatedRosters)
    }
  }

  // Filter out already selected options and format as IDropdownData
  const availableRosterOptions: IDropdownData[] = rosters
    .filter((opt) => !autoAcceptRosters.some((roster) => roster.id === opt.id))
    .map((roster) => ({
      value: roster.id,
      label: roster.name,
    }))

  return {
    selectedRosters,
    availableRosterOptions,
    handleRemoveTag,
    handleAddDropdownOption,
  }
}
